import { AllRekognitionMatchResult, Category, MatchResult, RekognitionItem } from "../types";

/** Quick-and-dirty singulariser: “keys” → “key”, “boxes” → “box”. */
function singularise(word: string): string {
	return word.replace(/(?:s|es)$/, "");
}

/** Normalises a string into lowercase, punctuation-free tokens. */
function normalizeText(text: string): string[] {
	return text
		.toLowerCase()
		.replace(/[^\w\s]/g, "") // remove punctuation
		.split(/[\s,/]+/) // split by space/comma/slash
		.map(singularise)
		.filter(Boolean);
}

function tokensToKey(tokens: string[]): string {
	return tokens.join(" "); // stable canonical form
}

/**
 * Returns match quality:
 *   3 = exact string (case-insensitive)     e.g. "Key" == "key"
 *   2 = exact token set (all tokens equal)  e.g. "wedding ring" ⇄ "ring wedding"
 *   2.5 = at least one identical token      e.g. "Key" in "Keys, Keyfob, badge"
 *   1 = fuzzy/prefix token match            e.g. "piano" in "pianos"
 *   0 = no match
 */
function getMatchScore(label: string, localizedValue: string): number {
	const labelNorm = label.trim().toLowerCase();
	const valueNorm = localizedValue.trim().toLowerCase();

	if (labelNorm === valueNorm) return 3;

	const labelTokens = normalizeText(label);
	const categoryTokens = normalizeText(localizedValue);

	const exactTokens =
		labelTokens.length === categoryTokens.length && tokensToKey(labelTokens) === tokensToKey(categoryTokens);
	if (exactTokens) return 2;

	const strongPartial = labelTokens.some((t) => categoryTokens.includes(t));
	if (strongPartial) return 2.5;

	const fuzzyPartial = labelTokens.some((l) => categoryTokens.some((c) => c.startsWith(l) || l.startsWith(c)));
	return fuzzyPartial ? 1 : 0;
}

type MatchWithScore = AllRekognitionMatchResult & { matchScore: number };

/**
 * Compare Rekognition labels with local categories, returning the `maxResults`
 * best matches (default 5).  Duplicates (same ItemCategory_ID) are collapsed,
 * and results are sorted by matchScore → confidence DESC.
 *
 * If you do **not** want final display-name deduplication, set
 * `dedupeDisplayName = false`.
 */
export function findAllRekognitionMatch(
	rekognitionData: RekognitionItem[],
	categories: Category[],
	maxResults: number = 5,
	dedupeDisplayName: boolean = true,
): AllRekognitionMatchResult[] {
	if (!rekognitionData?.length || !categories?.length) return [];

	const bestByCategory = new Map<string, MatchWithScore>();

	/* ---------- Pass 1: best hit per ItemCategory_ID ---------------- */
	for (let i = 0; i < rekognitionData.length; i++) {
		const rekognitionObject = rekognitionData[i];
		const labels = [rekognitionObject.name].concat(rekognitionObject.aliases ?? []);

		for (let j = 0; j < labels.length; j++) {
			const label = labels[j];

			for (let k = 0; k < categories.length; k++) {
				const category = categories[k];

				const matchScore = getMatchScore(label, category.LocalizedValue);
				if (matchScore === 0) continue;

				const candidate: MatchWithScore = {
					ItemCategory_ID: category.ItemCategory_ID,
					ItemCategory_fk: category.ItemCategory_fk,
					LocalizedValue: category.LocalizedValue,
					rekognitionObject,
					confidence: rekognitionObject.confidence,
					matchScore,
				};

				const current = bestByCategory.get(category.ItemCategory_ID);
				if (
					!current ||
					candidate.matchScore > current.matchScore ||
					(candidate.matchScore === current.matchScore && candidate.confidence > current.confidence)
				) {
					bestByCategory.set(category.ItemCategory_ID, candidate);
				}
			}
		}
	}

	/* ---------- Optional Pass 2: dedupe by display name ------------- */
	let matches: MatchWithScore[] = Array.from(bestByCategory.values());

	if (dedupeDisplayName) {
		const byName = new Map<string, MatchWithScore>();
		for (let i = 0; i < matches.length; i++) {
			const m = matches[i];
			const key = m.LocalizedValue.trim().toLowerCase();
			const existing = byName.get(key);

			if (
				!existing ||
				m.matchScore > existing.matchScore ||
				(m.matchScore === existing.matchScore && m.confidence > existing.confidence)
			) {
				byName.set(key, m);
			}
		}
		matches = Array.from(byName.values());
	}

	/* ---------- Sort & trim ----------------------------------------- */
	matches.sort((a, b) => b.matchScore - a.matchScore || b.confidence - a.confidence);

	if (maxResults > 0) matches = matches.slice(0, maxResults);

	/* ---------- Strip the helper field before returning -------------- */
	return matches.map(({ matchScore: _discard, ...clean }) => clean);
}
